import { useFormik } from 'formik'
import { FC, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ExameAsoDtoCreate, ExameAsoDtoUpdate, ExameAsoView } from '../../core/_models'
import {
  Button,
  FetchState,
  FormInput,
  FormSelect,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import {
  ButtonType,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { exameSchema } from './validation/exameSchema'
import { createExameCliente, editExameCliente } from '../../core/_request'
import { useT } from '_metronic/i18n/Metronici18n'

export const ExameClienteModal: FC = () => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()
  const [searchParams] = useSearchParams()

  const { data: exameAso } = (fetchStates['exame'] || {}) as FetchState<ExameAsoView>

  const idCliente = Number(searchParams.get('cliente'))
  const idEmpresa = Number(searchParams.get('empresa'))

  const initialValues: {
    setor: IOption<number> | undefined
    cargo: IOption<number> | undefined
    exame: string
  } = {
    setor: exameAso
      ? { label: exameAso.setor?.nome || '', value: exameAso.setor?.id as number }
      : undefined,
    cargo: exameAso
      ? { label: exameAso.cargo?.nome || '', value: exameAso.cargo?.id as number }
      : undefined,
    exame: exameAso?.exame || '',
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: exameSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (exameAso) {
          const exame: ExameAsoDtoUpdate = {
            cargo: values.cargo?.value as number,
            setor: values.setor?.value as number,
            exame: values.exame,
            ativo: exameAso.ativo,
          }
          await editExameCliente(exameAso.id, exame)
          showAlert({
            title: 'Sucesso!',
            text: 'Exame alterado com sucesso!',
            icon: 'success',
          })
        } else {
          const exame: ExameAsoDtoCreate = {
            cargo: values.cargo?.value as number,
            setor: values.setor?.value as number,
            exame: values.exame,
          }
          await createExameCliente(idCliente, exame)
          showAlert({ title: 'Sucesso!', text: 'Exame criado com sucesso!', icon: 'success' })
        }
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const extraParams = useMemo(
    () => ({ idEmpresa: Number(idEmpresa), idCliente: Number(idCliente) }),
    [idEmpresa, idCliente]
  )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      <FormSelect
        formik={formik}
        name='setor'
        label='Setor'
        placeholder='Selecione um setor'
        fetchOptions={OptionsHelper.getOptionsSetoresClientes}
        fetchOptionsParams={extraParams}
        isClearable={false}
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isRequired
      />
      <FormSelect
        formik={formik}
        name='cargo'
        label='Cargo'
        placeholder='Selecione um cargo'
        fetchOptions={OptionsHelper.getOptionsCargosClientes}
        fetchOptionsParams={extraParams}
        isClearable={false}
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isRequired
      />
      <FormInput
        formik={formik}
        name='exame'
        label='Exame'
        placeholder='Digite o nome do exame'
        isRequired
      />

      <div className='d-flex justify-content-center mt-10'>
        <Button type={ButtonType.Reset} onClick={closeModal} className='btn-secondary me-3'>
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary'>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.send')}
        </Button>
      </div>
    </form>
  )
}
